<template>
  <div
    :class="[
      $style.root,
      {
        [$style.moreGap]: isRecordView,
      },
    ]"
  >
    <div ref="itemsContainer" :class="[$style.items, { [$style.filterSetGap]: type === 'SCAFilterSetItem' }]">
      <template v-if="type === 'SCATag'">
        <SCATag
          v-for="(item, index) in items"
          :key="`item_${index}`"
          v-bind="item.props"
          :as="item.to ? props.linkComponent : undefined"
          :to="item.to"
          overflow="visible"
          data-sca-overflow-container-item
          @click="handleTagClick(item, $event)"
        >
          {{ item.label }}
        </SCATag>
      </template>
      <SCATabGroup
        v-else-if="type === 'SCATab'"
        :class="{ [$style.items]: true, [$style.lessGap]: lessGap }"
        :type="scaTabGroupType"
      >
        <template v-if="isRecordView && actions.length > 0">
          <div :class="$style.inner">
            <template v-for="(action, index) in actions" :key="`action_${index}`">
              <SCAIconButton
                v-if="!isDropdownTrigger(action)"
                :key="`action_${index}`"
                size="small"
                appearance="secondary"
                :class="{ [$style.hidden]: isRecordView && hiddenActionsIndices.has(index) }"
                v-bind="action.props"
                :icon="action?.props?.icon"
                :disabled="action?.props?.disabled ?? false"
                data-sca-overflow-container-item
                :data-sca-overflow-index="index"
                data-sca-overflow-type="action"
                @click.prevent.stop="handleItemClick(action)"
              />
              <SCADropdown
                v-else
                :key="`dropdown_action_${index}`"
                no-padding
                :class="{ [$style.hidden]: isRecordView && hiddenActionsIndices.has(index) }"
              >
                <template #trigger="{ target }">
                  <SCAIconButton
                    size="xsmall"
                    appearance="secondary"
                    icon="phosphor/dots-three"
                    :title="$t('common.label.options')"
                    :popovertarget="target"
                    data-sca-overflow-container-item
                    :data-sca-overflow-index="index"
                    data-sca-overflow-type="action"
                  />
                </template>
                <template #default="{ popover }">
                  <SCAButton
                    v-for="(drAction, drIndex) in dropdownActions"
                    :key="`dr_action_${drIndex}`"
                    v-bind="drAction.props"
                    :class="$style.action"
                    :disabled="drAction?.props?.disabled ?? false"
                    @click.prevent.stop="
                      handleItemClick(drAction);
                      popover?.hidePopover();
                    "
                  >
                    {{ drAction.label }}
                  </SCAButton>
                </template>
              </SCADropdown>
            </template>
          </div>
          <SCADivider direction="vertical" class="mx-component-gap-2xSmall" />
        </template>
        <SCATab
          v-for="(item, index) in tabItems"
          :key="`item_${index}`"
          v-bind="item.props"
          :label="item.label"
          :class="{ [$style.hidden]: isRecordView && hiddenIndices.has(index) }"
          data-sca-overflow-container-item
          :data-sca-overflow-index="index"
          data-sca-overflow-type="item"
          @click.prevent.stop="handleItemClick(item)"
        >
          <span :class="$style.labelWithCount">
            {{ item.label }}
            <span v-if="isRecordView && item.count" :class="$style.count">{{ item.count }}</span>
          </span>
        </SCATab>
      </SCATabGroup>
      <template v-else-if="hasSlot && actionSlots.length > 0">
        <template v-for="(slot, index) in actionSlots" :key="`slot_${index}`">
          <component :is="slot" v-if="slot.type === SCADivider" />
          <div
            v-else
            data-sca-overflow-container-item
            :data-sca-overflow-index="shouldIgnoreOverflow(slot) ? undefined : index"
            data-sca-overflow-type="action"
            :data-sca-overflow-grow="isGrowing(slot) || undefined"
            :class="{ [$style.hidden]: hiddenActionsIndices.has(index), [$style.grow]: isGrowing(slot) }"
          >
            <component :is="slot" />
          </div>
        </template>
      </template>
      <SCAFilterSetItem
        v-for="(item, index) in items"
        v-else-if="type === 'SCAFilterSetItem'"
        :key="`item_${index}`"
        v-bind="item.props"
        :as="item.href ? 'a' : undefined"
        :href="item.href"
        :class="{ [$style.hidden]: hiddenIndices.has(index) }"
        data-sca-overflow-container-item
        :data-sca-overflow-index="index"
        data-sca-overflow-type="item"
        @click="handleFilterSetItemClick(item, $event)"
        @edit="handleItemEdit(item)"
      >
        {{ item.label }}
      </SCAFilterSetItem>
      <SCAFilterSetItem
        v-for="(item, index) in props.filterItems ?? []"
        :key="`filterItem_${index}`"
        v-bind="item.props"
        :as="item.href ? 'a' : undefined"
        :href="item.href"
        :class="[$style.filterItem, { [$style.hidden]: hiddenFilterIndices.has(index) }]"
        data-sca-overflow-container-item
        :data-sca-overflow-index="index"
        data-sca-overflow-type="filter"
        @click="handleFilterSetItemClick(item, $event)"
        @edit="handleItemEdit(item)"
      >
        {{ item.label }}
      </SCAFilterSetItem>
    </div>
    <SCADropdown
      v-if="(hiddenItems && hiddenItems.length > 0) || hiddenActionSlots.length > 0 || hiddenFilterItemsList.length > 0"
      v-model:is-open="isOpenDropdown"
      no-padding
    >
      <template #trigger="{ target }">
        <button
          type="button"
          :popovertarget="target"
          :class="[$style.trigger, { [$style.recordTrigger]: isRecordView || hasSlot }]"
          :data-selected="hasSelectedHiddenItem"
          @click.stop
        >
          <SCASvgIcon icon="phosphor/dots-three" />
        </button>
      </template>
      <template #default="{ popover: parentPopover }">
        <div :class="[$style.hiddenItems, { [$style.hiddenItemsSlots]: hasSlot }]">
          <template v-if="type === 'SCAFilterSetItem'">
            <div
              v-for="(item, index) in hiddenItems"
              :key="`hiddenItem_${index}`"
              class="flex items-center gap-component-padding-xSmall"
            >
              <SCAFilterSetItem
                v-bind="item.props"
                :as="item.href ? 'a' : undefined"
                :href="item.href"
                :selected="item?.props?.selected ?? false"
                :disabled="item?.props?.disabled ?? false"
                @click="handleFilterSetItemClick(item, $event)"
                @edit="handleItemEdit(item)"
              >
                {{ item.label }}
              </SCAFilterSetItem>
            </div>
          </template>
          <template v-else-if="hasSlot">
            <template v-for="(slot, index) in hiddenActionSlots" :key="`overflow_slot_${index}`">
              <div data-sca-overflow-container-item :data-sca-overflow-index="index" data-sca-overflow-type="action">
                <SCAOverflowMenuScope :close="() => parentPopover?.hidePopover()">
                  <component :is="slot" />
                </SCAOverflowMenuScope>
              </div>
            </template>
            <div
              v-if="hiddenFilterItemsList.length > 0"
              :class="[$style.hiddenFilterItems, { [$style.hiddenFilterItemsBorder]: hiddenActionSlots.length > 0 }]"
            >
              <div
                v-for="(item, index) in hiddenFilterItemsList"
                :key="`hiddenFilter_${index}`"
                :class="$style.hiddenFilterItem"
              >
                <SCAFilterSetItem
                  v-bind="item.props"
                  :as="item.href ? 'a' : undefined"
                  :href="item.href"
                  :selected="item?.props?.selected ?? false"
                  :disabled="item?.props?.disabled ?? false"
                  @click="handleFilterSetItemClick(item, $event)"
                  @edit="handleItemEdit(item)"
                >
                  {{ item.label }}
                </SCAFilterSetItem>
              </div>
            </div>
          </template>
          <template v-else>
            <template v-if="isRecordView && hiddenActions.length > 0">
              <div :class="[$style.inner, { [$style.hide]: hiddenActions.length === 0 }]">
                <template v-for="(action, index) in hiddenActions" :key="`hidden_action_${index}`">
                  <SCAIconButton
                    v-if="!isDropdownTrigger(action)"
                    size="small"
                    appearance="secondary"
                    v-bind="action.props"
                    :icon="action?.props?.icon"
                    :disabled="action?.props?.disabled ?? false"
                    data-sca-overflow-container-item
                    :data-sca-overflow-index="action.__originalIndex ?? index"
                    data-sca-overflow-type="action"
                    @click.prevent.stop="
                      handleItemClick(action);
                      parentPopover?.hidePopover();
                    "
                  />
                  <SCADropdown v-else no-padding>
                    <template #trigger="{ target }">
                      <SCAIconButton
                        size="xsmall"
                        appearance="secondary"
                        icon="phosphor/dots-three"
                        :title="$t('common.label.options')"
                        :popovertarget="target"
                        data-sca-overflow-container-item
                        :data-sca-overflow-index="action.__originalIndex ?? index"
                        data-sca-overflow-type="action"
                      />
                    </template>
                    <template #default="{ popover }">
                      <SCAButton
                        v-for="(drAction, drIndex) in dropdownActions"
                        :key="`hidden_dr_action_${drIndex}`"
                        v-bind="drAction.props"
                        :class="$style.action"
                        :disabled="drAction?.props?.disabled ?? false"
                        @click.prevent.stop="
                          handleItemClick(drAction);
                          popover?.hidePopover();
                          parentPopover?.hidePopover();
                        "
                      >
                        {{ drAction.label }}
                      </SCAButton>
                    </template>
                  </SCADropdown>
                </template>
              </div>
              <SCADivider :class="[$style.divider, actions.length === 0 && $style.hide]" appearance="border" />
            </template>
            <SCAButton
              v-for="(item, index) in hiddenItems"
              :key="`hiddenItem_${index}`"
              size="small"
              intent="none"
              appearance="secondary"
              type="ghost"
              :class="{
                [$style.hiddenItemButton]: isRecordView,
                [$style.selected]: isRecordView && item?.props?.selected,
              }"
              :selected="item?.props?.selected ?? false"
              :disabled="item?.props?.disabled ?? false"
              @click.prevent.stop="
                handleItemClick(item);
                parentPopover?.hidePopover();
              "
            >
              <span :class="$style.labelWithCount">
                {{ item.label }}
                <span v-if="isRecordView && item.count" :class="$style.count">{{ item.count }}</span>
              </span>
            </SCAButton>
          </template>
        </div>
      </template>
    </SCADropdown>
  </div>
</template>
<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import type { Component, Slot, VNode } from "vue";
import { Comment, computed, nextTick, onBeforeUnmount, onMounted, ref, useSlots, useTemplateRef, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";

import SCADropdown from "./Dropdown/SCADropdown.vue";
import SCAButton from "./SCAButton.vue";
import SCADivider from "./SCADivider.vue";
import SCAFilterSetItem from "./SCAFilterSetItem.vue";
import SCAIconButton from "./SCAIconButton.vue";
import SCAOverflowMenuScope from "./SCAOverflowMenuScope.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATab from "./SCATab.vue";
import SCATabGroup from "./SCATabGroup.vue";
import SCATag from "./SCATag.vue";
import type { SCAButtonProps, SCAFilterSetItemProps, SCAIconButtonProps, SCATabProps, SCATagProps } from "./types";

interface OverFlowItem {
  label: string;
  count?: string;
  onClick?: () => unknown;
  onEdit?: () => unknown;
  props?: SCATagProps | SCATabProps | SCAFilterSetItemProps | SCAButtonProps | SCAIconButtonProps;
  isDropdownTrigger?: boolean;
  to?: RouteLocationRaw;
  href?: string;
}

interface SCAOverflowContainerProps {
  type?: "SCATag" | "SCATab" | "SCAFilterSetItem";
  items?: OverFlowItem[];
  actions?: OverFlowItem[];
  dropdownActions?: OverFlowItem[];
  isRecordView?: boolean;
  lessGap?: boolean;
  filterItems?: OverFlowItem[];
  linkComponent?: string | Component;
  observeItems?: boolean;
}

const props = withDefaults(defineProps<SCAOverflowContainerProps>(), {
  type: undefined,
  items: () => [],
  actions: () => [] as OverFlowItem[],
  dropdownActions: () => [] as OverFlowItem[],
  isRecordView: false,
  lessGap: false,
  filterItems: () => [] as OverFlowItem[],
  linkComponent: undefined,
  observeItems: false,
});

const isOpenDropdown = ref(false);
const hiddenIndices = ref<Set<number>>(new Set());
const hiddenActionsIndices = ref<Set<number>>(new Set());
const hiddenFilterIndices = ref<Set<number>>(new Set());

const itemsContainerRef = useTemplateRef<HTMLDivElement>("itemsContainer");
const hiddenItems = computed(() => props.items.filter((_, i) => hiddenIndices.value.has(i)));

const tabItems = computed(() => props.items.map((i) => ({ ...i, props: i.props as SCATabProps })));

const scaTabGroupType = computed(() =>
  tabItems.value.length > 0 && tabItems.value[0]?.props && "type" in tabItems.value[0].props
    ? tabItems.value[0].props.type
    : "default",
);

type ActionItem = OverFlowItem & { props?: SCAIconButtonProps; __originalIndex?: number; isDropdownTrigger?: boolean };
type DropdownActionItem = OverFlowItem & { props?: SCAButtonProps };

const dropdownActions = computed<DropdownActionItem[]>(() => props.dropdownActions as DropdownActionItem[]);
const actions = computed<ActionItem[]>(() => props.actions as ActionItem[]);

const isDropdownTrigger = (action: OverFlowItem | ActionItem) => {
  return action?.isDropdownTrigger === true;
};

const hiddenActions = computed<ActionItem[]>(() => {
  if (!props.actions || hiddenActionsIndices.value.size === 0) return [];

  return Array.from(hiddenActionsIndices.value)
    .sort((a, b) => a - b)
    .map((idx) => {
      const action = props.actions?.[idx];
      return action ? ({ ...action, __originalIndex: idx } as ActionItem) : undefined;
    })
    .filter((item): item is ActionItem => Boolean(item));
});

const hasSelectedHiddenItem = computed(() => {
  return hiddenItems.value?.some((item) => item?.props?.selected === true);
});

const slots = useSlots() as Record<string, Slot | undefined>;

const actionSlots = computed<VNode[]>(() => {
  return (slots.default?.() ?? []).filter((vnode) => vnode.type !== Comment);
});
const hasSlot = computed(() => actionSlots.value.length > 0);

const hiddenActionSlots = computed(() => {
  if (hiddenActionsIndices.value.size === 0) return [];
  return actionSlots.value.filter(
    (slot: VNode, index: number) => !shouldIgnoreOverflow(slot) && hiddenActionsIndices.value.has(index),
  );
});

const hiddenFilterItemsList = computed(() =>
  (props.filterItems ?? []).filter((_, i) => hiddenFilterIndices.value.has(i)),
);

const hasSlotAttribute = (slot: VNode, attribute: string) => {
  if ("props" in slot && slot.props && attribute in slot.props) {
    return true;
  }
  return "type" in slot && typeof slot.type === "object" && "props" in slot.type && attribute in slot.type.props;
};

const shouldIgnoreOverflow = (slot: VNode) => hasSlotAttribute(slot, "ignore-overflow");
const isGrowing = (slot: VNode) => hasSlotAttribute(slot, "overflow-grow");

const handleItemClick = (item: OverFlowItem) => {
  if (typeof item?.onClick === "function") {
    item.onClick();
  }
};

const handleTagClick = (item: OverFlowItem, event: MouseEvent) => {
  if (item.to) return;
  event.preventDefault();
  event.stopPropagation();
  handleItemClick(item);
};

const isModifiedClick = (event: MouseEvent) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;

const handleFilterSetItemClick = (item: OverFlowItem, event: MouseEvent) => {
  if (item.href && isModifiedClick(event)) return;
  event.preventDefault();
  event.stopPropagation();
  handleItemClick(item);
};

const handleItemEdit = (item: OverFlowItem) => {
  if (typeof item?.onEdit === "function") {
    item.onEdit();
  }
};

const setIfChanged = (target: { value: Set<number> }, next: Set<number>) => {
  const prev = target.value;
  if (prev.size === next.size && [...next].every((i) => prev.has(i))) return false;
  target.value = next;
  return true;
};

interface HiddenIndices {
  items: Set<number>;
  actions: Set<number>;
  filters: Set<number>;
}

function measureHidden(): HiddenIndices {
  const newHiddenIdx = new Set<number>();
  const newHiddenActionIdx = new Set<number>();
  const newHiddenFilterIdx = new Set<number>();

  const container = itemsContainerRef.value;
  if (container) {
    const icBounding = container.getBoundingClientRect();

    container.querySelectorAll<HTMLElement>("[data-sca-overflow-container-item]").forEach((el) => {
      const idxAttr = el.getAttribute("data-sca-overflow-index");
      const idx = idxAttr !== null ? parseInt(idxAttr, 10) : -1;
      if (idx < 0) return;

      const cBounding = el.getBoundingClientRect();
      const pos = cBounding.left - icBounding.left + cBounding.width;
      // A growing item ends right at the edge while it fits; a zoomed page leaves it a fraction over.
      const tolerance = el.hasAttribute("data-sca-overflow-grow") ? -0.5 : 1;
      if (icBounding.width - pos >= tolerance) return;

      const type = el.getAttribute("data-sca-overflow-type") ?? "item";
      if (type === "action") newHiddenActionIdx.add(idx);
      else if (type === "filter") newHiddenFilterIdx.add(idx);
      else newHiddenIdx.add(idx);
    });
  }

  return { items: newHiddenIdx, actions: newHiddenActionIdx, filters: newHiddenFilterIdx };
}

function applyHidden(hidden: HiddenIndices) {
  const itemsChanged = setIfChanged(hiddenIndices, hidden.items);
  const actionsChanged = setIfChanged(hiddenActionsIndices, hidden.actions);
  const filtersChanged = setIfChanged(hiddenFilterIndices, hidden.filters);
  return itemsChanged || actionsChanged || filtersChanged;
}

let applyFrame = 0;
let pendingHidden: HiddenIndices | undefined;

const scheduleApply = (hidden: HiddenIndices) => {
  pendingHidden = hidden;
  if (applyFrame) return;
  applyFrame = requestAnimationFrame(() => {
    applyFrame = 0;
    if (pendingHidden) applyHidden(pendingHidden);
    pendingHidden = undefined;
  });
};

let settleGeneration = 0;

async function settle() {
  const generation = ++settleGeneration;
  cancelAnimationFrame(applyFrame);
  applyFrame = 0;
  pendingHidden = undefined;
  for (let pass = 0; pass < 3; pass++) {
    if (!applyHidden(measureHidden())) return;
    await nextTick();
    if (generation !== settleGeneration) return;
  }
}

const itemResizeObserver = new ResizeObserver(() => scheduleApply(measureHidden()));

function observeItemSizes() {
  itemResizeObserver.disconnect();
  if (!props.observeItems) return;
  itemsContainerRef.value
    ?.querySelectorAll<HTMLElement>("[data-sca-overflow-container-item]")
    .forEach((el) => itemResizeObserver.observe(el));
}

onMounted(() => {
  observeItemSizes();
  void settle();
});

watch(
  [() => props.items, () => props.filterItems, () => actionSlots.value, () => props.observeItems],
  () => {
    observeItemSizes();
    void settle();
  },
  { flush: "post" },
);

useResizeObserver(itemsContainerRef, () => scheduleApply(measureHidden()));

onBeforeUnmount(() => {
  cancelAnimationFrame(applyFrame);
  itemResizeObserver.disconnect();
});
</script>
<style lang="scss" module>
.root {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  min-width: 0;
  width: 100%;
  height: 100%;

  &.moreGap {
    gap: var(--space-component-gap-xLarge);
  }
}

.items {
  display: flex;
  align-items: center;
  gap: var(--sca-overflow-container-gap, var(--space-component-gap-small));
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
  flex-grow: 1;
  position: relative;
  height: 100%;
}

.filterSetGap {
  gap: var(--sca-overflow-container-gap, 20px);
}

.lessGap {
  gap: 0;
}

.labelWithCount {
  display: inline-flex;
  align-items: center;
  gap: var(--space-component-gap-xSmall);
}

.count {
  color: var(--color-content-tertiary);
  font-weight: 450;
}

.hiddenItems {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: var(--space-component-gap-xSmall);
  min-width: 160px;
}

.hiddenItemsSlots {
  align-items: center;
  flex-wrap: wrap;
  flex-direction: unset;
  gap: var(--space-component-gap-small);
  min-width: fit-content;
}

.filterItem + .filterItem {
  margin-left: 12px;
}

.hiddenFilterItems {
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: var(--space-component-gap-xSmall);
  margin: 0 -8px;
  padding: 0 8px;
}

.hiddenFilterItemsBorder {
  border-top: 1px solid var(--color-border-default);
  padding-top: var(--space-component-gap-xSmall);
}

.hiddenFilterItem {
  display: flex;
  align-items: center;
  gap: var(--space-component-padding-xSmall);
}

.hiddenItemButton {
  width: 100%;
  text-align: left;
  padding: 0 var(--space-component-padding-small) !important;

  &.selected:not(:disabled) {
    color: var(--color-content-informative-primary) !important;
    background-color: var(--color-background-informative-onSubtle) !important;
  }
}

.trigger {
  display: flex;
  justify-content: center;
  align-items: center;
  width: var(--size-2xSmall);
  height: 100%;
  padding: 0;
  color: inherit;
  cursor: pointer;
  border: none;

  background-color: transparent;
  border-radius: var(--border-radius-medium);

  &:hover {
    background-color: var(--color-coolGrey-p4);
  }

  &[data-selected="true"] {
    background-color: var(--color-coolGrey-p3);
  }

  svg {
    width: 14px;
    height: 14px;
    cursor: pointer;

    path {
      cursor: pointer;
    }
  }
}

.recordTrigger {
  width: 28px;
  height: 28px;
  border-radius: var(--border-radius-large);

  svg {
    width: 20px;
    height: 20px;
    cursor: pointer;
  }
}

.grow {
  flex: 1 1 0;
  > * {
    contain: inline-size;
  }
}

.hidden {
  visibility: hidden;
}

.divider {
  width: calc(100% + 16px);
  margin: var(--space-component-padding-xSmall) -8px !important;
}

.action {
  height: 32px !important;
  min-width: 124px;
  text-align: left;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
}

.inner {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
}

.hide {
  display: none;
}
</style>
